import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, Menu, ChevronRight, FlaskConical, Sun } from "lucide-react";
import { api } from "../../lib/api";
import { WORLDS, worldPath, ENVS } from "../../lib/worlds";
import { useApp, ROLE_LABEL } from "../../context/AppCtx";
import { useFetch } from "../../lib/hooks";
import { ago } from "../../lib/fmt";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const t = useRef();
  useEffect(() => {
    clearTimeout(t.current);
    if (q.length < 2) { setRes(null); return; }
    t.current = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)), 250);
  }, [q]);
  return (
    <div className="relative flex-1 max-w-xl">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
      <input data-testid="topbar-global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opportunities, companies, projects, investors…" className="input pl-9 bg-[#FAF9F5]" />
      {res && (
        <div className="absolute top-12 left-0 right-0 card p-2 z-[1200] max-h-[380px] overflow-y-auto scroll-thin" data-testid="global-search-results">
          <div className="px-2 py-1 text-[10px] text-[#8A847C] uppercase tracking-wider">Scope: {res.scope.join(", ")} {res.excluded.length > 0 && `· isolated: ${res.excluded.join(", ")}`}</div>
          {res.results.length === 0 && <div className="p-3 text-sm text-[#8A847C]">No authorised matches.</div>}
          {res.results.map((r) => (
            <button key={r.id} data-testid={`search-result-${r.id}`} onClick={() => { setQ(""); nav(worldPath(r)); }} className="w-full text-left px-2 py-2 rounded-lg hover:bg-[#FAF9F5] flex items-center gap-2">
              <span className="pill bg-[#F4F1EA] border-[#E5E0D5] text-[#57534E]">{WORLDS[r.world]?.short}</span><span className="text-[13px] font-medium truncate">{r.name}</span><span className="ml-auto text-[11px] text-[#A8A29E]">{r.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { data } = useFetch("/activity?limit=12");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="topbar-notifications" className="relative w-10 h-10 grid place-items-center rounded-xl hover:bg-[#F4F1EA]"><Bell size={17} /><span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#C86D51]" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 z-[1300]">
        <div className="px-4 py-3 border-b font-serif font-semibold">Activity & notifications</div>
        <div className="max-h-[360px] overflow-y-auto scroll-thin">
          {(data || []).map((a) => (
            <div key={a.id} className="px-4 py-2.5 border-b border-[#F4F1EA] text-[12.5px]"><div className="font-medium">{a.action}</div><div className="text-[11px] text-[#8A847C]">{WORLDS[a.world]?.short} · {a.role} · {ago(a.at)}</div></div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Topbar({ onMenu }) {
  const { role, setRole, env, setEnv } = useApp();
  const navTo = useNavigate();
  const loc = useLocation();
  const parts = loc.pathname.split("/").filter(Boolean);
  const W = WORLDS[parts[0]];
  const mod = W?.modules.find((m) => m.id === parts[1]);
  const [clock, setClock] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setClock(new Date()), 30000); return () => clearInterval(i); }, []);
  return (
    <header className="h-16 sticky top-0 z-[1100] bg-white/90 backdrop-blur-md border-b border-[#E5E0D5] flex items-center gap-3 px-4 lg:px-6">
      <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-button"><Menu size={20} /></button>
      <nav className="hidden xl:flex items-center gap-1 text-[12px] text-[#8A847C] min-w-[220px]" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#1C1917]">Afrikho</Link>
        {(W || parts[0]) && <><ChevronRight size={12} /><Link to={`/${parts[0]}`} className="hover:text-[#1C1917] capitalize">{W?.short || parts[0]}</Link></>}
        {mod && <><ChevronRight size={12} /><span className="text-[#1C1917] font-medium">{mod.label}</span></>}
      </nav>
      <GlobalSearch />
      <div className="hidden md:flex items-center gap-1.5 pill bg-amber-500/10 text-amber-800 border-amber-300" data-testid="demo-environment-badge"><FlaskConical size={12} />Demonstration Environment</div>
      <Notifications />
      <select data-testid="topbar-environment-selector" value={env} onChange={(e) => { const E = ENVS[e.target.value]; setEnv(e.target.value, E.role); navTo(E.home); }} className="input !w-[120px] sm:!w-[220px] shrink py-2 font-semibold text-[12.5px] cursor-pointer">
        {Object.entries(ENVS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <select data-testid="topbar-role-switcher" value={role} onChange={(e) => setRole(e.target.value)} className="input !w-[110px] sm:!w-[180px] shrink py-2 font-semibold text-[12.5px] cursor-pointer">
        {ENVS[env].roles.map((k) => <option key={k} value={k}>{ROLE_LABEL[k]}</option>)}
      </select>
      <div className="hidden 2xl:hidden items-center gap-3 text-[12px] text-[#57534E] pl-2 border-l"><div><div className="font-semibold tnum">{clock.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}</div><div className="text-[10px] text-[#A8A29E]">{clock.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" })}</div></div><Sun size={16} className="text-[#C5A059]" /><div className="text-[11px]">Johannesburg<br /><span className="text-[#A8A29E]">illustrative</span></div></div>
    </header>
  );
}
